import { useContext, useEffect, useState } from "react";

import CheckboxInput from "../../../../../shared/components/checkbox-input/checkbox-input";
import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import AuthContext from "../../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../../shared/definitions/datatypes";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { failedToast, getData, postSingle } from "../../../../../shared/utils/api";
import { backendDataTypeToFrontend, IObject, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ColumnSelector from "../../../column-selector/column-selector";
import { IColumn } from "../../../data-table/data-table";
import SearchInput from "../../../search-input/search-input";
import { IWidgetModalProps } from "../../widget-utils";
import { IReferenceWidget } from "../../widgets/reference-widget/reference-widget";
import Spinner from "@/shared/components/spinner/spinner";
import { IFieldWidget } from "../../widgets/text-field-widget/text-field-widget";
import "./reference-component-modal.scss";

export interface IReferenceComponentFieldModal {
  type: "reference";
  datamodelId?: string;
  datamodelType: "event" | "entity" | "alertcase";
}

interface IReferenceComponentFieldModalProps extends IWidgetModalProps<IReferenceWidget> {
  selectedDatamodelId?: string;
  datamodelType: "event" | "entity" | "alertcase";
}

const ReferenceComponentFieldModal = ({
  selectedDatamodelId,
  modalOpen,
  widgetData,
  datamodelType,
  onWidgetChanged
}: IReferenceComponentFieldModalProps) => {
  const emptyReferenceWidget: IFieldWidget = {
    id: "",
    label: "",
    modalType: "reference",
    type: "field",
    required: false,
    readonly: false,
    primaryKey: false,
    datamodel_type: "",
    datamodel_id: "",
    field_id: "",
    modalData: {
      datamodelType: datamodelType
    }
  };
  
  const authCtx = useContext(AuthContext);

  const [modalData, setModalData] = useState<any>(widgetData?.id ? widgetData : {...emptyReferenceWidget});  // eslint-disable-line
  const [showModal, setShowModal] = useState(modalOpen || false);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<IColumn[]>([]);
  const [datamodelFields, setDatamodelFields] = useState<IEnumerationOption[]>([]);
  const [referenceDatamodelFields, setReferenceDatamodelFields] = useState<{key: string, dataType: string}[]>([]);
  
  const { sendRequest: getDatamodelDetails, data: getDatamodelData, status: getDatamodelStatus, error: getDatamodelError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");
  const { sendRequest: getReferenceDatamodelDetails, data: getReferenceDatamodelData, status: getReferenceDatamodelStatus, error: getReferenceDatamodelError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");
  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(postSingle, false, authCtx.token, "datamodelTable", "platform");

  useEffect(() => {
    setShowModal(modalOpen || false);
    setModalData(widgetData?.id ? widgetData : {...emptyReferenceWidget});
    setSelectedColumns(widgetData?.selectedColumns || []);
  }, [modalOpen, widgetData]);

  useEffect(() => {
    if (selectedDatamodelId) {
      getDatamodelDetails(`datamodel_handle_referencing_field/${datamodelType}/${selectedDatamodelId}?only_referencing=true`);
    }
  }, [selectedDatamodelId]);

  useEffect(() => {
    if (getDatamodelData && !getDatamodelError && getDatamodelStatus === "completed") {
      setDatamodelFields(getDatamodelData.fields.map((field : IObject) => ({
        id: field.input_field_name,
        name: field.field_name
      })));
    }
  }, [getDatamodelData, getDatamodelStatus, getDatamodelError]);

  useEffect(() => {
    if (getReferenceDatamodelData && !getReferenceDatamodelError && getReferenceDatamodelStatus === "completed") {
      setReferenceDatamodelFields(getReferenceDatamodelData.fields.map((field : IObject) => ({
        key: field.input_field_name,
        dataType: backendDataTypeToFrontend(field.field_datatype)
      })));
    }
  }, [getReferenceDatamodelData, getReferenceDatamodelError, getReferenceDatamodelStatus]);

  useEffect(() => {
    if (!widgetData?.reference_id) {
      return;
    }

    getTableData(`datamodel/table/${widgetData?.reference_id}/entity`, {
      columns: [],
      showData: false,
      rowData: []
    }, undefined, "none");
  }, [widgetData?.reference_id]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      const columns = Object.entries(getTableDataResult.columns).map(([key, value]) => ({
        key: key,
        displayName: value,
        dataType: {type: referenceDatamodelFields.find((field) => field.key === key)?.dataType || "text"}
      }) as IColumn);

      setColumns(columns);
    }
  }, [getTableDataResult, getTableDataStatus, getTableDataError]);
  
  const handleOnDatamodelChange = (selectedOption: IEnumerationOption | undefined) => {
    if (!selectedOption) {
      return;
    }

    const fieldInputNames = selectedOption.id.split(',');
    const datamodelField = getDatamodelData?.fields?.find((field : IObject) => 
      field.input_field_name === fieldInputNames[0] || field.input_field_name === selectedOption?.id
    );
    
    if (!datamodelField) {
      return;
    }
    
    modalData.primaryKey = datamodelField.primary_key;
    modalData.datamodel_type = datamodelType;
    modalData.datamodel_id = selectedDatamodelId;
    
    if (datamodelField.composite_field_ids && datamodelField.composite_field_ids.length > 1) {
      modalData.field_id = datamodelField.composite_field_ids; // Store as array
      modalData.is_composite_key = true;
    } else {
      modalData.field_id = datamodelField.id;
      modalData.is_composite_key = false;
    }
    
    modalData.type = "reference";
    modalData.reference_id = datamodelField.target_datamodel_id;
    modalData.selectedColumns = [];
    modalData.id = selectedOption?.id || "";
    modalData.modalData["datamodelFieldId"] = selectedOption?.id || "";
    modalData.label = selectedOption?.name || "";
    modalData.grouping_id = datamodelField.grouping_id;

    setSelectedColumns([]);
    
    getTableData(`datamodel/table/${datamodelField.target_datamodel_id}/entity`, {
      columns: [],
      showData: false,
      rowData: []
    }, undefined, "none");
    
    getReferenceDatamodelDetails(`datamodel/entity/${datamodelField.target_datamodel_id}`);

    setModalData({...modalData});
  };

  const handleColumnSelectionChanged = (selectedColumns: IColumn[]) => {
    const columns: IColumn[] = selectedColumns.map((column) => {
      const fieldDataType = referenceDatamodelFields.find((field) => field.key === column.key)?.dataType;
      
      return {
        key: column.key,
        displayName: column.displayName,
        dataType: {type: fieldDataType || "text"}
      } as IColumn;
    });

    setSelectedColumns(columns);
    setModalData({...modalData, selectedColumns: columns});
  };

  const renderModalBody = () => {
    return <div>
      <SearchInput
        label="Reference entity"
        handleOnChange={handleOnDatamodelChange}
        data={datamodelFields}
        inputKey="datamodelFieldId"
        required={true}
        selectedOption={modalData?.modalData?.datamodelFieldId || ""}
      />
      <CheckboxInput
        label="Required"
        checked={modalData.required}
        onChange={() => setModalData({
          ...modalData,
          required: !modalData.required
        })}
      />
      <CheckboxInput
        label="Readonly"
        checked={modalData.readonly}
        onChange={() => setModalData({
          ...modalData,
          readonly: !modalData.readonly
        })}
      />
      <div className="referenceComponentModal-columnSelector">
        { columns.length !== 0 && <ColumnSelector 
          availableColumns={columns} 
          selectedColumns={selectedColumns} 
          onSelectionChanged={handleColumnSelectionChanged} 
        />}
      </div>
    </div>;
  };

  const handleModalSubmit = () => {
    if (modalData.selectedColumns.length === 0) {
      failedToast("Please select at least one column");
      return;
    }

    onWidgetChanged(modalData as IReferenceWidget);
    setShowModal(false);
    setModalData({...emptyReferenceWidget});
    setSelectedColumns([]);
    setColumns([]);
  };

  const handleModalClosed = () => {
    onWidgetChanged(undefined);
    setShowModal(false);
    setModalData({...emptyReferenceWidget});
    setSelectedColumns([]);
    setColumns([]);
  };

  const openModelFunction = () => {
    return () => {
      setShowModal(true);
    };
  }

  return <div className='referenceComponentModal'>
    {validateLoadingStatusNew([getDatamodelStatus, getReferenceDatamodelStatus, getTableDataStatus]) && <Spinner />}
    <div className="referenceComponentModal-layoutButton" onClick={openModelFunction()}>
      Reference entity +
    </div>

    <SimpleModal
      allowScrollOnBody={false}
      actionButtonDisabled={selectedColumns.length === 0}
      mode="Reference entity" 
      onModalClickHandler={noop} 
      modalBody={renderModalBody()} 
      buttonText={"Add"} 
      handleModalClosed={handleModalClosed} 
      handleSubmit={handleModalSubmit} 
      show={showModal}
    />
  </div>;
};

export default ReferenceComponentFieldModal;